/**
 * WorkspaceList — workspace listing screen (five-branch Variant A).
 *
 * Route: /workspaces
 * Composite: workspaceHome.list
 *
 * Layout matches the v2 design handoff (#workspaces). The list composite only
 * supplies a workspace's name/slug today, so every other card field the design
 * shows (initiative count, description, members, runs/wk, weekly spend) has no
 * backend source yet and is rendered with the red `workspaces__missing`
 * treatment so the gap is obvious. See outputs/workspaces-compare/DIFF_REPORT.md.
 *
 * FR-009, FR-028.
 */
import '../../styles/screens/workspaces.css';
import React, { useState } from "react";
import SubscriptionScope from "../../components/layout/SubscriptionScope";
import { useEffectiveUser } from "../../api/hooks/useEffectiveUser";
import { useWorkspaceHome } from "../../api/hooks/useWorkspaceHome";
import { useCreateWorkspace } from "../../api/hooks/useCreateWorkspace";
import { useScreenTTI } from "../../lib/useScreenTTI";
import { Link, useHashLocation } from "../../lib/router";
import { Button } from "../../components/primitives/Button";

export default function WorkspaceList(): React.ReactElement {
  const user = useEffectiveUser();
  const scopeKey = user?._id ?? "anon";

  return (
    <SubscriptionScope active key={scopeKey}>
      <WorkspaceListBody />
    </SubscriptionScope>
  );
}

/** Inline red flag for a field the current backend cannot supply. */
function Missing({ label, reason }: { label: string; reason: string }): React.ReactElement {
  return (
    <span className="workspaces__missing" title={`Missing backend data: ${reason}`}>
      {label}
    </span>
  );
}

function WorkspaceListBody(): React.ReactElement {
  const state = useWorkspaceHome("list");
  const isReady = state.status === "ready";
  useScreenTTI("workspace-list", { enabled: isReady });

  const { create, isCreating, error: createError } = useCreateWorkspace();
  const [, navigate] = useHashLocation();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    const result = await create(trimmed);
    if (result) {
      setName("");
      setShowForm(false);
      // Open the new workspace so the user sees it immediately.
      navigate(`/workspaces/${result.workspaceId}`);
    }
  }

  if (state.status === "loading") {
    return (
      <div data-testid="workspace-list-skeleton" role="status" aria-label="Loading workspaces">
        <p>Loading workspaces...</p>
      </div>
    );
  }

  if (state.status === "empty") {
    return (
      <div data-testid="workspace-list-empty">
        <h2>No workspaces</h2>
        <p>Create a workspace to get started.</p>
      </div>
    );
  }

  if (state.status === "forbidden") {
    return (
      <div data-testid="workspace-list-forbidden" role="alert">
        <h2>Access restricted</h2>
        <p>You do not have permission to view workspaces.</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div data-testid="workspace-list-error" role="alert">
        <h2>Something went wrong</h2>
        <p>{state.error}</p>
        <button type="button" onClick={() => window.location.reload()} aria-label="Retry loading workspaces">
          Retry
        </button>
      </div>
    );
  }

  // ready
  const { data } = state;
  if (data.mode !== "list") throw new Error("Unexpected mode: " + data.mode);
  const view = data.data as {
    workspaces: Array<{ id: string; name: string; slug: string }>;
    totalCount: number;
  };
  const count = view.totalCount ?? view.workspaces.length;

  return (
    <div className="workspaces__root" data-testid="workspace-list-page">
      <header className="workspaces__header">
        <span className="workspaces__eyebrow">Workspaces</span>
        <div className="workspaces__header-row">
          <div>
            <h1 className="workspaces__heading">Workspaces</h1>
            <p className="workspaces__subtitle">
              {count} workspace{count === 1 ? "" : "s"}. Each one keeps its own initiatives, members, and budgets.
            </p>
          </div>
          <div className="workspaces__header-actions">
            {showForm ? (
              <form className="workspaces__create-form" onSubmit={handleCreate}>
                <input
                  className="workspaces__create-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Workspace name"
                  aria-label="Workspace name"
                  maxLength={80}
                  autoFocus
                  disabled={isCreating}
                />
                <Button type="submit" variant="primary" disabled={isCreating || name.trim().length === 0}>
                  {isCreating ? "Creating…" : "Create"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setShowForm(false); setName(""); }}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
              </form>
            ) : (
              <Button
                variant="primary"
                icon="plus"
                aria-label="New workspace"
                onClick={() => setShowForm(true)}
              >
                New workspace
              </Button>
            )}
          </div>
        </div>
        {createError && (
          <p className="workspaces__create-error" role="alert">
            {createError.message ?? "Could not create workspace."}
          </p>
        )}
      </header>

      <ul className="workspaces__grid" role="list">
        {view.workspaces.map((ws) => (
          <li key={ws.id}>
            <Link href={`/workspaces/${ws.id}`} className="workspaces__card" aria-label={ws.name}>
              {/* initiative count not returned by the list composite */}
              <Missing label="— initiatives" reason="initiative count (only in workspace detail mode)" />
              <h3 className="workspaces__card-title">{ws.name}</h3>
              {/* repositories table has no description field */}
              <p className="workspaces__card-desc workspaces__missing" title="Missing backend data: no description field on the workspace">
                No description — backend field missing
              </p>
              <div className="workspaces__card-footer">
                {/* member list not returned by the list composite */}
                <Missing label="members ?" reason="member list (only in workspace detail mode)" />
                {/* no weekly runs / spend aggregation exists */}
                <Missing label="— runs / wk · $—" reason="weekly runs count and spend aggregation" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
